import { useState } from "react";
import { Swords } from "lucide-react";
import { fmtUSD, entityLabel } from "@/lib/format";

/**
 * Outbid-style rank claim widget, FLIP-adapted:
 * pick a target rank, see the exact USD needed to take it,
 * and jump straight into backing the entity that can get there.
 */
export default function RankEstimator({ entities, onClaim }) {
  // Default to the boldest move: the crown.
  const [target, setTarget] = useState(1);

  const ranked = (entities || []).filter((e) => e.status === "active");
  if (ranked.length < 2) return null;

  const maxTarget = Math.min(ranked.length - 1, 10);
  const k = Math.min(Math.max(target, 1), maxTarget);

  // To claim rank k, back the entity sitting right below it (index k).
  const chaser = ranked[k];
  const target_ = ranked[k - 1];
  const amount = chaser?.gap_to_next;

  if (!chaser || !amount || !target_) return null;
  const chaserLabel = entityLabel(chaser);
  const targetLabel = entityLabel(target_);
  const currentRank = chaser.leaderboard_rank || chaser.rank || k + 1;
  const amountLabel = fmtUSD(amount);

  // "+" climbs toward #1 (costlier), "−" slides down to cheaper spots.
  const climb = () => setTarget((t) => Math.max(t - 1, 1));
  const descend = () => setTarget((t) => Math.min(t + 1, maxTarget));

  return (
    <div className="rounded-2xl border border-gold/25 bg-gradient-to-b from-gold/[0.07] to-transparent px-6 py-5" data-testid="rank-estimator">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[240px] flex-1">
          <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
            <Swords className="h-4 w-4" strokeWidth={2} />
            Rank takeover
          </div>
          <div className="font-display text-xl font-black uppercase tracking-tight sm:text-2xl">
            Move {chaserLabel} from #{currentRank} to <span className="text-gold">#{k}</span>
          </div>
          <p className="mt-1 text-sm text-zinc-400" data-testid="estimator-hint">
            Pass <span className="font-bold text-white">{targetLabel}</span>
            <span className="mx-2 text-zinc-700">·</span>
            <span className="font-mono font-bold text-gold">{amountLabel} required</span>
            {k === 1 && " · Take the crown 👑"}
          </p>
        </div>

        <div className="flex items-center gap-2" aria-label="Choose target rank">
          <button onClick={descend} disabled={k >= maxTarget} data-testid="rank-descend"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 transition-colors hover:bg-white/10 disabled:opacity-30"
            aria-label="Cheaper rank">−</button>
          <span className="text-gold" data-testid="estimator-rank" aria-live="polite" aria-atomic="true">#{k}</span>
          <button onClick={climb} disabled={k <= 1} data-testid="rank-climb"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 transition-colors hover:bg-white/10 disabled:opacity-30"
            aria-label="Higher rank">+</button>
        </div>

        <div className="rounded-xl border border-gold/20 bg-black/30 px-4 py-2 text-center">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">Required bid</div>
          <div className="font-mono text-2xl font-black text-gold" data-testid="estimator-amount">{amountLabel}</div>
        </div>

        <button
          data-testid="estimator-claim"
          onClick={() => onClaim(chaser, amount)}
          className="w-full rounded-full bg-white px-6 py-2.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 sm:ml-auto sm:w-auto"
        >
          Back {chaserLabel} · {amountLabel}
        </button>
      </div>
    </div>
  );
}
